import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetOrdersQuery } from "../../redux/api/ordersApi";
import { 
  Pagination, 
  Table, 
  Button, 
  Tag, 
  Typography, 
  Alert, 
  Space, 
  Select 
} from "antd";
import { EyeOutlined, EditOutlined } from "@ant-design/icons";
import { formatPrice } from "../../utils/currencyUtils";

const { Title, Text } = Typography;

const OrdersPage = () => {
  const navigate = useNavigate();
  const { data: orders = [], isLoading, error } = useGetOrdersQuery();
  const [filter, setFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // Ensure orders is always an array
  const safeOrders = Array.isArray(orders) ? orders : [];

  const filteredOrders =
    filter === "All" ? safeOrders : safeOrders.filter((o) => o.status === filter);

  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);

  // Table columns configuration
  const columns = [
    {
      title: 'ID',
      dataIndex: 'order_id',
      key: 'order_id',
      width: '10%',
    },
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: '25%',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '15%',
      render: (status) => {
        let color = 'default';
        if (status === 'Completed') color = 'success';
        else if (status === 'Pending') color = 'warning';
        else if (status === 'Cancelled') color = 'error';
        else if (status === 'Processing') color = 'processing';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Total',
      dataIndex: 'total_price',
      key: 'total_price',
      width: '15%',
      render: (price) => formatPrice(price, 'CZK'),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: '15%',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '20%',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/orders/${record.order_id}`)}
            title="View"
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/orders/${record.order_id}/edit`)}
            title="Edit"
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Alert title="Loading orders..." type="info" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error loading orders"
        description={error?.data?.message || error?.error || 'Failed to load orders'}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={1} style={{ margin: 0 }}>Orders</Title>
      </div>

      {/* Filter */}
      <Space style={{ marginBottom: 16 }}>
        <Text>Filter by Status:</Text>
        <Select
          value={filter}
          onChange={setFilter}
          style={{ width: 150 }}
          size="large"
        >
          <Select.Option value="All">All</Select.Option>
          <Select.Option value="Pending">Pending</Select.Option>
          <Select.Option value="Completed">Completed</Select.Option>
          <Select.Option value="Cancelled">Cancelled</Select.Option>
          <Select.Option value="Processing">Processing</Select.Option>
        </Select>
      </Space>

      {safeOrders.length === 0 ? (
        <Alert
          message="No orders found"
          description={filter !== "All" ? 'No orders match the selected filter.' : 'No orders available.'}
          type="info"
          showIcon
        />
      ) : (
        <>
          <Table
            columns={columns}
            dataSource={paginatedOrders}
            rowKey="order_id"
            pagination={false}
            size="middle"
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ marginTop: 16 }}>
              <Pagination
                current={currentPage}
                total={filteredOrders.length}
                pageSize={rowsPerPage}
                onChange={setCurrentPage}
                hideOnSinglePage={true}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OrdersPage;
